import { useEffect, useState } from "react";
import { FileText, ExternalLink, X, Loader2 } from "lucide-react";

import { viewResumeUrl } from "../../../Common/Recruitment/Services/RecruitmentService";

const ViewResumeDrawer = ({ open, onClose, resumeUrl, candidateName }) => {
  const [mounted, setMounted] = useState(false);
  const [active, setActive] = useState(false);

  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
  const loadPdf = async () => {
    if (!open) return;

    // Always clear previous resume first
    setBlobUrl(null);
    setFailed(false);

    // No resume uploaded
    if (!resumeUrl) {
      setFailed(true);
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(viewResumeUrl(resumeUrl));

      if (!res.ok) {
        setFailed(true);
        return;
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);

      setBlobUrl(url);
    } catch (err) {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  loadPdf();
}, [open, resumeUrl]);

  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  useEffect(() => {
    let frame;
    let closeTimer;

    if (open) 
    {
      setMounted(true);

      frame = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setActive(true);
        });
      });
    } 
    else 
    {
      setActive(false);

      closeTimer = setTimeout(() => {
        setMounted(false);
      }, 500);
    }

    return () => {
      if (frame) cancelAnimationFrame(frame);
      clearTimeout(closeTimer);
    };
  }, [open]);

  if (!mounted) return null;

  return (
    <div
      className={`fixed inset-0 z-[70] ${active ? "pointer-events-auto" : "pointer-events-none"}`}
    >
      <div
        onClick={onClose}
        className={`
          absolute inset-0 bg-black/40 backdrop-blur-sm
          transition-opacity duration-500 ease-out
          ${active ? "opacity-100" : "opacity-0"}
        `}
      />

      <div
        className={`
          fixed top-0 right-0 h-full w-full max-w-6xl bg-white shadow-2xl flex flex-col overflow-hidden
          transform-gpu transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]
          ${active ? "translate-x-0 opacity-100" : "translate-x-10 opacity-0"}
        `}
      >
        <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-blue-100/70 blur-3xl pointer-events-none" />

        <div className="relative border-b border-slate-200 bg-white/90 backdrop-blur-xl px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center shadow-sm">
              <FileText className="text-red-600" size={28} />
            </div>

            <div>
              <h2 className="text-[22px] font-bold tracking-tight text-slate-800">
                Candidate Resume
              </h2>

              <p className="text-sm text-slate-500 mt-1">
                {candidateName || "Resume Preview"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => blobUrl && window.open(blobUrl, "_blank")}
              className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:shadow-sm active:scale-[0.97] transition-all duration-200"
            >
              <ExternalLink size={16} />
              Open
            </button>

            <button
              onClick={onClose}
              className="h-11 w-11 rounded-xl border border-slate-200 bg-white flex items-center justify-center hover:bg-red-50 hover:border-red-100 hover:text-red-600 active:scale-[0.94] transition-all duration-200"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="relative flex-1 overflow-hidden bg-slate-100">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white">
              <Loader2 className="animate-spin text-blue-600" size={30} />
            </div>
          )}

          {blobUrl && !failed && (
            <iframe
              key={blobUrl}
              title="Resume"
              src={blobUrl}
              className="h-full w-full border-0 bg-white"
            />
          )}

          {failed && (
            <div className="absolute inset-0 flex items-center justify-center bg-white">
              <div className="text-center">
                <p className="text-slate-700 font-medium">
                  Resume not available
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewResumeDrawer;
